/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { DeviceType } from '../../../enums/device-type.enum';
import { DialogContentType } from '../../../enums/dialog-type.enum';
import { PaymentSourceEnums, PaymentTransactionType } from '../../../enums/payment-method.enum';
import { CreateTransactionResponse } from '../../../payment/models/create-transaction-response-model';
import { PaymentData, PaymentMetaData, PaymentMinMaxBoundary } from '../../../payment/models/payment-data.model';
import { PaymentAppPayOutput, TransactionResultEnum, VerifyTransaction } from '../../../payment/models/paymentOutput';
import { Processors } from '../../../payment/models/processors.interface';
import { PorTransactionService } from '../../../por-transaction.service';
import { LoggerService, NotificationService } from '../../../services';
import { AppFacadeService } from '../../../services/app-facade.service';
import { AppMediatorService } from '../../../services/app-mediator.service';
import { ContractService } from '../../../services/contract.service';
import { selectIsMobile } from '../../app-wide/app/app.selectors';
import { AppState } from '../../app-wide/app/app.state';
import { ConfigActionTypes } from '../../app-wide/config/config.actions';
import { selectConfig, selectCustomerId, selectRouteParams } from '../../app-wide/config/config.selectors';
import { showDialog } from '../../app-wide/dialog/dialog.actions';
import { areContractsValidForMultiPayment, getContractsTotal, getSelectedContracts, getSelectedContractsModels, getTotalAmountDueForContracts } from '../contract/contract.selectors';
import { MULTI_CONTRACT_MAX_SELECTION_ALLOWED } from '../../../constants/default.const';
import { getCustomer } from '../customer/customer.selectors';
import { closePayment, ContractActionTypes, setSelectedContracts } from './../contract/contract.actions';
import {
    createTransaction,
    crossAppEventEmitted,
    failCreateTransaction,
    failLoadPaymentBoundaries,
    failVerifyPayment,
    loadPaymentProcessorAvailability,
    loadPaymentProcessorAvailabilityFailure,
    loadPaymentProcessorAvailabilitySuccess,
    PaymentActionTypes,
    sendPaymentToRMS,
    sendPaymentToRMSFailure,
    sendPaymentToRMSSuccess,
    setConfigData,
    setDialogEnabled,
    setMultiPaymentBoundaries,
    setPaymentAppInput,
    setPaymentSectionEnabled,
    setTransactionLoaded,
    successCreateTransaction,
    successVerifyPayment
} from './../payment/payment.actions';
import { selectDefaultPaymentMid, selectIsPaymentProcessorAvailable, selectMultipaymentBoundaries, selectPaymentAppOutput } from './payment.selectors';

@Injectable()
export class PaymentEffects {
    private readonly actions$ = inject(Actions);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly translateService = inject(TranslateService);
    private readonly porTransactionService = inject(PorTransactionService);
    private readonly notification = inject(NotificationService);
    private readonly logger = inject(LoggerService);
    private readonly contractService = inject(ContractService);
    private readonly store = inject<Store<AppState>>(Store);

    syncFailedPaymentWithRMS$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendPaymentToRMS),
            switchMap(() =>
                this.appMediatorService.porPayService.syncPaymentWithRMS().pipe(
                    map(() => {
                        return sendPaymentToRMSSuccess();
                    }),
                    catchError(() => of(sendPaymentToRMSFailure()))
                )
            )
        )
    );

    /**
     * Load payment boundaries based on selected contracts
     * - Single contract: GET /get-boundary/{contractId} (direct, faster, includes proper MID)
     * - Multiple contracts: POST /get-multi-boundary (loop, aggregate)
     * This ensures single contract payments don't show unnecessary MID dropdown
     */
    loadPaymentBoundaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActionTypes.MultiPaymentContinue),
            tap(() => {
                this.appFacadeService.setLoading(true);
            }),
            withLatestFrom(this.store.select(getSelectedContracts)),
            withLatestFrom(this.store.select(getSelectedContractsModels)),
            withLatestFrom(this.store.select(getContractsTotal)),
            withLatestFrom(this.store.select(selectCustomerId)),
            withLatestFrom(this.store.select(selectRouteParams)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([[[[[_action, selectedContracts], contractModels], total], customerId], _routeParams]) => {
                const isSingleContract = selectedContracts.length === 1;

                // Multi-contract validation (using selector projectors)
                if (!isSingleContract) {
                    // Validate: Max 50 contracts
                    if (selectedContracts.length > MULTI_CONTRACT_MAX_SELECTION_ALLOWED) {
                        this.appFacadeService.setLoading(false);
                        this.notification.error(
                            this.translateService.instant('MaximumContractsExceeded', {
                                max: MULTI_CONTRACT_MAX_SELECTION_ALLOWED,
                                provided: selectedContracts.length
                            })
                        );
                        return of(
                            failLoadPaymentBoundaries({
                                error: `Maximum ${MULTI_CONTRACT_MAX_SELECTION_ALLOWED} contracts allowed. Selected ${selectedContracts.length} contracts.`
                            })
                        );
                    }

                    // Validate: Contracts eligible using selector
                    const validation = areContractsValidForMultiPayment(selectedContracts).projector(contractModels);
                    if (!validation.valid) {
                        this.appFacadeService.setLoading(false);
                        this.notification.error(
                            this.translateService.instant('InvalidContractsForMultiPayment', {
                                invalid: validation.invalidCount,
                                total: selectedContracts.length,
                                contractIds: validation.invalidContracts.map(c => c.contractId).join(', ')
                            })
                        );
                        return of(
                            failLoadPaymentBoundaries({
                                error: `${validation.invalidCount} contracts not eligible (must be Closed status with balance > 0, no pending payment)`
                            })
                        );
                    }

                    // Validate: Total AmountDue > 0 using selector
                    const totalDue = getTotalAmountDueForContracts(selectedContracts).projector(contractModels);
                    if (totalDue <= 0) {
                        this.appFacadeService.setLoading(false);
                        this.notification.error(
                            this.translateService.instant('MultiContractTotalMustBePositive', {
                                total: totalDue
                            })
                        );
                        return of(
                            failLoadPaymentBoundaries({
                                error: `Cannot process payment. Total AmountDue is $${totalDue}. Must be greater than $0.`
                            })
                        );
                    }

                    // Multi-contract: ALWAYS uses sum of AmountDue (ignore routeParams.amount)
                    // No amount validation needed - system calculates automatically

                    // All validations passed - proceed with multi-boundary API call
                    return this.appMediatorService.porPayService.getPaymentBoundaryMultiContract(customerId, selectedContracts, total).pipe(
                        map((paymentBoundaries: PaymentMinMaxBoundary[]) => {
                            this.appFacadeService.setLoading(false);
                            return setMultiPaymentBoundaries({ paymentBoundaries });
                        }),
                        catchError(error => {
                            this.appFacadeService.setLoading(false);
                            return of(failLoadPaymentBoundaries({ error: error.message }));
                        })
                    );
                }

                // Single contract: Use direct endpoint (no validation needed)
                const contractId = selectedContracts[0];
                return this.appMediatorService.porPayService.getMinMaxFromPaymentBoundary(customerId, contractId).pipe(
                    map((paymentBoundaries: PaymentMinMaxBoundary[]) => {
                        this.appFacadeService.setLoading(false);
                        return setMultiPaymentBoundaries({ paymentBoundaries });
                    }),
                    catchError(error => {
                        this.appFacadeService.setLoading(false);
                        return of(failLoadPaymentBoundaries({ error: error.message }));
                    })
                );
            })
        )
    );

    onSetMultiboundaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.SetMultiPaymentBoundaries),
            withLatestFrom(this.store.select(selectMultipaymentBoundaries)),
            withLatestFrom(this.store.select(getSelectedContracts)),
            withLatestFrom(this.store.select(selectRouteParams)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([[[_action, paymentBoundaries], selectedContracts], routeParams]) => {
                if (paymentBoundaries) {
                    const isSingleContract = selectedContracts.length === 1;
                    let amount: string | null = null;

                    // For single contract, determine amount based on boundaries
                    if (isSingleContract) {
                        const minBoundary = paymentBoundaries[0]?.minAmount || 0;
                        const maxBoundary = paymentBoundaries[0]?.maxAmount || 0;

                        // AC: If min === max, only one valid amount (ignore any provided amount)
                        if (minBoundary === maxBoundary && maxBoundary > 0) {
                            amount = maxBoundary.toString();
                        } else {
                            // User has choice (min < max)
                            const providedAmount = routeParams?.amount;

                            if (providedAmount !== undefined && providedAmount !== null) {
                                // Amount provided - validate it's within range
                                if (providedAmount >= minBoundary && providedAmount <= maxBoundary) {
                                    // Valid amount within range - use it
                                    amount = providedAmount.toString();
                                } else {
                                    // Outside range - use maxBoundary
                                    amount = maxBoundary.toString();
                                }
                            } else {
                                // No amount provided - use maxBoundary
                                amount = maxBoundary.toString();
                            }
                        }
                    }

                    return of(
                        createTransaction({
                            amount,
                            transactionType: isSingleContract ? PaymentTransactionType.Single : PaymentTransactionType.Multiple
                        })
                    );
                }
                return of();
            })
        )
    );

    resetPaymentBoundaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActionTypes.PaymentWindowClosed),
            map(() => setMultiPaymentBoundaries({ paymentBoundaries: null }))
        )
    );

    setConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ConfigActionTypes.SetConfig),
            map(action => setConfigData({ config: action }))
        )
    );

    createTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.CreateTransaction),
            tap(() => {
                this.appFacadeService.setLoading(true);
            }),
            withLatestFrom(
                this.store.select(getSelectedContracts),
                this.store.select(getSelectedContractsModels),
                this.store.select(getContractsTotal),
                this.store.select(selectConfig),
                this.appFacadeService.getCurrency(),
                this.store.select(selectCustomerId),
                this.store.select(selectIsMobile),
                this.store.select(selectDefaultPaymentMid),
                this.store.select(selectIsPaymentProcessorAvailable)
            ),
            switchMap(([action, selectedContracts, selectedContractsModels, total, config, currencyCode, customerId, isMobile, defaultPaymentMid, processors]) => {
                const { amount, transactionType } = action;
                const toPay = amount ? amount : total ? total.toString() : '0';
                const amountToPay: string = this.appMediatorService.porPayService.formatCurrencyForPorPay(toPay);
                setTransactionLoaded({ transactionLoaded: false });
                const metadata: PaymentMetaData = {
                    source: this.getPaymentSource(config),
                    actionType: transactionType,
                    averageContractValue: parseFloat(toPay) / selectedContracts.length, // Will safely handle toPay = 0
                    deviceType: isMobile ? DeviceType.Mobile : DeviceType.Desktop,
                    paymentAllocation: selectedContracts.length,
                    customerId: customerId,
                    contractIds: selectedContracts.join(',')
                };
                const paymentMid = this.appMediatorService.porPayService.getPaymentMid(selectedContractsModels[0]?.depotId, selectedContracts, processors, defaultPaymentMid);

                return this.appMediatorService.porPayService
                    .createPayment(customerId, currencyCode, Number(amountToPay), config.organizationId ?? '', paymentMid, selectedContracts, Number(amountToPay), metadata)
                    .pipe(
                        switchMap((res: unknown) => {
                            this.appFacadeService.setLoading(false);
                            return of(successCreateTransaction({ res }));
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return [
                                setTransactionLoaded({ transactionLoaded: true }),
                                failCreateTransaction({ error: [this.translateService.instant('CreateTransactionFailed'), error?.message, error?.error?.token, error?.error?.message].join(' / ') }),
                                setDialogEnabled({ dialogEnabled: true }),
                                crossAppEventEmitted({
                                    event: {
                                        error: {
                                            code: error?.status,
                                            message: error?.error?.message ?? error?.message
                                        }
                                    }
                                })
                            ];
                        })
                    );
            })
        )
    );

    successTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.SuccessCreateTransaction),
            switchMap((action: { type: string; res: unknown }) => {
                const response: CreateTransactionResponse = action.res as CreateTransactionResponse;
                if (response.token && response.token === 'NO_PAYMENT_METHOD_AVAILABLE') {
                    return [
                        setTransactionLoaded({ transactionLoaded: true }),
                        failCreateTransaction({ error: this.translateService.instant('CreateTransactionFailed') + this.translateService.instant('NoPaymentMethodAvailable') }),
                        setDialogEnabled({ dialogEnabled: true })
                    ];
                }

                return this.appMediatorService.porPayService.getPaymentConfiguration(response?.accessToken, response?.transactionId).pipe(
                    switchMap(configuration => {
                        return [setPaymentAppInput({ paymentAppInput: JSON.stringify(configuration) }), setTransactionLoaded({ transactionLoaded: true })];
                    })
                );
            })
        )
    );

    paymentFinished$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.PaymentFinished),
            withLatestFrom(this.store.select(getSelectedContractsModels)),
            withLatestFrom(this.store.select(getCustomer)),
            withLatestFrom(this.store.select(selectConfig)),
            withLatestFrom(this.store.select(selectPaymentAppOutput)),
            withLatestFrom(this.store.select(selectCustomerId)),
            withLatestFrom(this.store.select(selectRouteParams)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([[[[[[_action, selectedContractsModels], customer], config], paymentAppOutput], customerId], routeParams]) => {
                this.logger.logInfo(paymentAppOutput?.detail);
                const detail: PaymentAppPayOutput = paymentAppOutput?.detail as PaymentAppPayOutput;
                if (!detail?.TransactionId) {
                    return [
                        failVerifyPayment({ error: this.translateService.instant('MissingTransactionId') }),
                        setDialogEnabled({ dialogEnabled: true }),
                        crossAppEventEmitted({
                            event: {
                                error: {
                                    code: 400,
                                    message: this.translateService.instant('MissingTransactionId')
                                }
                            }
                        })
                    ];
                }
                if (
                    detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.APPROVED ||
                    detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.APPROVEDWITHWARNING ||
                    detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.PENDING
                ) {
                    this.notification.success(detail?.Message);
                    this.notification.success(this.translateService.instant('Please wait till we verify the received payment') + '.');

                    this.appFacadeService.setLoading(true);
                    const paymentSource = this.getPaymentSource(config);
                    const paymentData: PaymentData = this.appMediatorService.porPayService.getPaymentData(
                        customer,
                        config.organizationId ?? '',
                        detail,
                        selectedContractsModels,
                        paymentSource,
                        config
                    );

                    return this.appMediatorService.porPayService.verifyPayment(customerId, paymentData).pipe(
                        switchMap((res: VerifyTransaction) => {
                            this.logger.logWarning(res);
                            this.appFacadeService.setLoading(false);
                            this.porTransactionService.setTransactionSuccess(true);
                            if (routeParams === undefined) {
                                this.contractService.loadContracts({ customerId, search: paymentData?.contractId ?? paymentData?.contractIds?.join(',') });
                            }
                            return [
                                successVerifyPayment({ lastPaymentDetail: res.data ?? detail }),
                                closePayment(),
                                setPaymentSectionEnabled({ enabled: false }),
                                setSelectedContracts({ selectedContracts: [] }),
                                showDialog({ dialogContentType: DialogContentType.PaymentSuccess }),
                                crossAppEventEmitted({
                                    event: {
                                        response: detail
                                    }
                                })
                            ];
                        }),
                        catchError(error => {
                            this.logger.logError(error, '', true);
                            this.appFacadeService.setLoading(false);
                            return [
                                failVerifyPayment({ error: error?.message || this.translateService.instant('errorMessage') }),
                                setDialogEnabled({ dialogEnabled: true }),
                                crossAppEventEmitted({
                                    event: {
                                        error: {
                                            code: error?.status,
                                            message: error?.message || this.translateService.instant('errorMessage')
                                        }
                                    }
                                })
                            ];
                        })
                    );
                }

                let errorMessage = this.translateService.instant('PaymentTransactionErroredMessage');
                if (detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.CANCELLED) {
                    errorMessage = this.translateService.instant('PaymentTransactionCancelledMessage');
                } else if (detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.DECLINED) {
                    errorMessage = this.translateService.instant('PaymentTransactionDeclinedMessage');
                }
                return [
                    successVerifyPayment({ lastPaymentDetail: detail }),
                    failVerifyPayment({ error: errorMessage }),
                    setPaymentSectionEnabled({ enabled: false }),
                    showDialog({ dialogContentType: DialogContentType.PaymentSuccess }),
                    crossAppEventEmitted({
                        event: {
                            error: {
                                code: 400,
                                message: errorMessage
                            }
                        }
                    }),
                    closePayment()
                ];
            })
        )
    );

    loadPaymentProcessorAvailability$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadPaymentProcessorAvailability),
            switchMap(({ customerId }) =>
                this.appMediatorService.porPayService.isPaymentProcessorAvailable(customerId).pipe(
                    map((processors: boolean | Processors[]) => loadPaymentProcessorAvailabilitySuccess({ processors })),
                    catchError(() => of(loadPaymentProcessorAvailabilityFailure()))
                )
            )
        )
    );

    private getPaymentSource(config: { sourceApp?: string }): PaymentSourceEnums {
        let paymentSource = PaymentSourceEnums.ConsumerPortal;

        if (config.sourceApp?.length) {
            paymentSource = config.sourceApp as PaymentSourceEnums;
        } else if (this.appFacadeService.versionToggleService.isQuickLinkVersion()) {
            paymentSource = PaymentSourceEnums.QuickLink;
        }

        return paymentSource;
    }
}
